import { defineNuxtPlugin } from '#app';

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const recaptchaConfig = config.public.recaptchaEnterprise;

  nuxtApp.provide(
    'recaptchaEnterprise',
    new RecaptchaEnterpriseClient(recaptchaConfig)
  );
});

const ENTERPRISE_API_URL = 'https://www.google.com/recaptcha/enterprise.js';

function EventBus() {
  const subscriptions = {};

  this.subscribe = function (eventType, callback) {
    const id = Symbol('id');
    if (!subscriptions[eventType]) subscriptions[eventType] = {};
    subscriptions[eventType][id] = callback;
    return {
      unsubscribe: function () {
        delete subscriptions[eventType][id];
        if (
          Object.getOwnPropertySymbols(subscriptions[eventType]).length === 0
        ) {
          delete subscriptions[eventType];
        }
      }
    };
  };

  this.publish = function (eventType, arg) {
    if (!subscriptions[eventType]) return;

    Object.getOwnPropertySymbols(subscriptions[eventType]).forEach((key) =>
      subscriptions[eventType][key](arg)
    );
  };
}

class RecaptchaEnterpriseClient {
  constructor({ siteKey, hideBadge, language }) {
    if (!siteKey) {
      throw new Error('ReCaptcha error: No key provided');
    }

    this.siteKey = siteKey;
    this.hideBadge = hideBadge;
    this.language = language;
    this._elements = {};
    this._grecaptcha = null;
    this._eventBus = null;
    this._ready = false;
  }

  destroy() {
    if (this._ready) {
      this._ready = false;

      const { head } = document;
      const { style } = this._elements;

      const scripts = [...document.head.querySelectorAll('script')].filter(
        (script) => script.src.includes('recaptcha')
      );

      if (scripts.length) {
        scripts.forEach((script) => head.removeChild(script));
      }

      if (head.contains(style)) {
        head.removeChild(style);
      }

      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        badge.remove();
      }
    }
  }

  async execute(action) {
    try {
      await this.init();

      if ('grecaptcha' in window) {
        return this._grecaptcha.execute(this.siteKey, { action });
      }
    } catch (error) {
      throw new Error(`ReCaptcha error: Failed to execute ${error}`, {
        cause: error
      });
    }
  }

  getResponse(widgetId) {
    return new Promise((resolve, reject) => {
      if ('grecaptcha' in window) {
        const response = this._grecaptcha.getResponse(widgetId);

        if (response) {
          this._eventBus.publish('recaptcha-success', response);
          resolve(response);
        } else {
          const errorMessage = 'Failed to execute';

          this._eventBus.publish('recaptcha-error', errorMessage);
          reject(errorMessage);
        }
      }
    });
  }

  init() {
    if (this._ready) {
      return this._ready;
    }

    this._eventBus = new EventBus();
    this._elements = {
      script: document.createElement('script'),
      style: document.createElement('style')
    };

    const { script, style } = this._elements;

    script.setAttribute('async', '');
    script.setAttribute('defer', '');

    const params = [`render=${this.siteKey}`];
    if (this.language) {
      params.push(`hl=${this.language}`);
    }

    script.setAttribute('src', `${ENTERPRISE_API_URL}?${params.join('&')}`);

    window.recaptchaSuccessCallback = (token) =>
      this._eventBus.publish('recaptcha-success', token);
    window.recaptchaExpiredCallback = () =>
      this._eventBus.publish('recaptcha-expired');
    window.recaptchaErrorCallback = () =>
      this._eventBus.publish('recaptcha-error', 'Failed to execute');

    this._ready = new Promise((resolve, reject) => {
      script.addEventListener('load', () => {
        if (this.hideBadge) {
          style.innerHTML = '.grecaptcha-badge { display: none }';
          document.head.appendChild(style);
        }

        this._grecaptcha = window?.grecaptcha.enterprise || window?.grecaptcha;
        this._grecaptcha.ready(resolve);
      });

      script.addEventListener('error', () => {
        document.head.removeChild(script);
        reject('ReCaptcha error: Failed to load script');
        this._ready = null;
      });

      document.head.appendChild(script);
    });

    return this._ready;
  }

  on(event, callback) {
    return this._eventBus.subscribe(event, callback);
  }

  reset(widgetId) {
    if (typeof widgetId !== 'undefined') {
      this._grecaptcha.reset(widgetId);
    }
  }

  render(reference, { sitekey, theme }) {
    return this._grecaptcha.render(reference.$el || reference, {
      sitekey,
      theme
    });
  }
}
